import axiosRetry from 'axios-retry'

export default {
    async download(client, url, file_name = null, retryDelay = 4000) {
        // NOTE: This is NOT the way to do it, we are downloading the file in memory and then creating a link to it
        // Since the download is a stream, we should allow the browser to download it directly, the problem is that we need to create
        // a single-use jwt/token to allow for the authenticated download without requiring a header
        // for now we are accepting that the file will be relatively small and that this is not a big problem

        const response = await client.axios.get(url, {
            responseType: 'blob',
            // override retry policy as download needs more time before retry
            'axios-retry': {
                retries: 3,
                retryDelay: (retryNumber) => Math.pow(2, retryNumber) * retryDelay,
                retryCondition: axiosRetry.isNetworkOrIdempotentRequestError
            }
        })
        this.downloadFileFromResponse(response, file_name)
    },

    downloadFileFromResponse(response, file_name) {
        // NOTE: The request must have "{responseType: 'blob'}" in order to work
        const type = response.headers['content-type']
        const blob = new Blob([response.data], {type: type})
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)

        if (file_name === null || file_name === undefined) {
            const headerLine = response.headers['content-disposition']
            file_name = headerLine.split('filename=')[1].replace(/"/g, '')
        }

        link.download = file_name
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    },

    downloadBlob(data, fileName, mimeType = 'application/octet-stream', {bom = false} = {}) {
        // bom=true prepends UTF-8 BOM so Excel detects encoding correctly for accented chars
        const parts = bom ? ['﻿', data] : [data]
        const blob = new Blob(parts, {type: mimeType})
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
    },

    downloadFromUrl(url, fileName) {
        // Use iframe to avoid page navigation that interrupts MQTT connection
        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        iframe.src = url
        document.body.appendChild(iframe)
        // Clean up iframe after generous timeout (5 minutes) to handle slow server responses
        // The download continues independently once the browser receives the response headers
        setTimeout(() => {
            if (iframe.parentNode) {
                document.body.removeChild(iframe)
            }
        }, 300000)
    },

    async multiDownload(files, delayMs = 100, onProgress = null) {
        // Download multiple files sequentially with delay to avoid Firefox NS_BINDING_ABORTED
        // files: [{url: '...', filename: '...'}, ...]
        // delayMs: delay between downloads in milliseconds (default 100ms)
        // onProgress: optional callback(current, total, filename) for progress updates
        //
        // Firefox cancels downloads when too many are triggered rapidly.
        // Solution: Sequential downloads with delay between each
        // Reference: https://stackoverflow.com/questions/56245040/firefox-downloading-multiple-files-at-once-not-possible
        // Stack Overflow: "works using 1-3ms with few files, needs 100ms+ with more files"

        if (!files || files.length === 0) return

        const total = files.length

        for (let i = 0; i < files.length; i++) {
            const file = files[i]
            try {
                // Notify progress before download
                if (onProgress) {
                    onProgress(i + 1, total, file.filename)
                }

                this.downloadFromUrl(file.url, file.filename)

                // Wait after each download (including last) to let browser process
                await new Promise(resolve => setTimeout(resolve, delayMs))
            } catch (error) {
                console.error(`Failed to download ${file.filename}:`, error)
            }
        }
    }
}